import { useEffect, useRef, useState } from "react";
import type { SupportedLocale } from "../../lib/localeRoutes";
import { securityUtils } from "../../lib/securityUtils";
import { ALLERGENS, findAllergen } from "./catalog";
import { getBrowserTranslator, translateAllergen } from "./translation";
import type { CardEntry } from "./layout";

export interface LanguagePair { source: SupportedLocale; target: SupportedLocale }
export interface CustomAllergy {
  id: string;
  original: string;
  language: SupportedLocale;
  names: Partial<Record<SupportedLocale, string>>;
}

export function useAllergyCard(locale: SupportedLocale) {
  const [pair, setPair] = useState<LanguagePair>({ source: locale, target: locale === "tr" ? "en" : "tr" });
  const [selected, setSelected] = useState<string[]>(["peanut", "sesame"]);
  const [custom, setCustom] = useState<CustomAllergy[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<"unavailable" | "failed" | null>(null);
  const request = useRef(0);
  const sequence = useRef(0);
  const customIds = useRef(new Set<string>());
  useEffect(() => () => { request.current++; }, []);

  const toggle = (id: string) => setSelected(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]);
  const remove = (id: string) => {
    customIds.current.delete(id);
    const remaining = custom.filter(entry => entry.id !== id);
    setCustom(remaining);
    if (remaining.every(entry => entry.names[pair.source] && entry.names[pair.target])) setError(null);
  };

  async function addCustom(raw: string): Promise<boolean> {
    const text = securityUtils.sanitizeInput(raw);
    if (!text || text.length > 48) return false;
    const known = findAllergen(text, pair.source);
    if (known && (selected.includes(known.id) || custom.some(entry => findAllergen(entry.original, entry.language)?.id === known.id))) return true;
    const token = ++request.current;
    setError(null);
    setBusy(true);
    try {
      const translated = await translateAllergen(text, pair.source, pair.target, getBrowserTranslator());
      if (token !== request.current) return false;
      const names = known ? known.names : { [pair.source]: text, [pair.target]: translated };
      const entry = { id: `custom-${++sequence.current}`, original: text, language: pair.source, names };
      customIds.current.add(entry.id);
      setCustom(previous => [...previous, entry]);
      return true;
    } catch (cause) {
      if (token === request.current) setError(cause instanceof Error && cause.message === "translation_unavailable" ? "unavailable" : "failed");
      return false;
    } finally {
      if (token === request.current) setBusy(false);
    }
  }

  async function changePair(next: LanguagePair) {
    const token = ++request.current;
    setPair(next);
    setError(null);
    setBusy(custom.length > 0);
    const provider = getBrowserTranslator();
    const results = await Promise.all(custom.map(async entry => {
      const names = { ...entry.names };
      try {
        await Promise.all([next.source, next.target].map(async language => {
          if (!names[language]) names[language] = await translateAllergen(entry.original, entry.language, language, provider);
        }));
        return { ...entry, names };
      } catch {
        return { ...entry, names };
      }
    }));
    if (token !== request.current) return;
    const retained = results.filter(result => customIds.current.has(result.id));
    setCustom(retained);
    if (retained.some(entry => !entry.names[next.source] || !entry.names[next.target])) setError(provider ? "failed" : "unavailable");
    setBusy(false);
  }

  function setLanguage(side: keyof LanguagePair, language: SupportedLocale) {
    const next = { ...pair, [side]: language };
    if (next.source === next.target) next[side === "source" ? "target" : "source"] = pair[side];
    void changePair(next);
  }

  const entries: CardEntry[] = ALLERGENS.filter(item => selected.includes(item.id)).map(item => ({
    id: item.id, primary: item.names[pair.target], secondary: item.names[pair.source],
  }));
  for (const item of custom) {
    entries.push({ id: item.id, primary: item.names[pair.target] || "…", secondary: item.names[pair.source] || item.original });
  }
  const canDownload = entries.length > 0 && !busy && !error && custom.every(entry => entry.names[pair.source] && entry.names[pair.target]);

  return { pair, selected, custom, name, setName, busy, error, entries, canDownload, toggle, remove, addCustom, setLanguage, clearError: () => setError(null), swap: () => changePair({ source: pair.target, target: pair.source }) };
}

export type AllergyCardState = ReturnType<typeof useAllergyCard>;
